import { useCallback, useEffect, useMemo, useState } from "react";

import { Flex, Box, Divider, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router";

import { InventoryChangeReason } from "__graphql__/types";
import { GuardrailMap, PageName } from "analytics/events";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import { ProductNotAssignedForTheHubErrorModal } from "flows/Inbound/components/ProductNotAssignedForTheHubErrorModal";
import StockChangeConfirmationModal from "flows/Inventory/shared/components/StockChangeConfirmationModal";
import { useGetProductStockQuery } from "flows/Inventory/shared/queries/inventoryEntry/inventoryEntry.generated";
import { Page } from "shared/components/Page";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCountryNames } from "shared/hooks/useCountryNames";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { Button } from "ui/Button/Button";
import { NavigationHeaderShelfNumber } from "ui/NavigationHeaderShelfNumber/NavigationHeaderShelfNumber";
import { ProductFacing } from "ui/ProductFacing/ProductFacing";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { useRestockingServiceStore } from "../../RestockingList/stores/restockingServiceStore";
import { StockAdjustmentPanel } from "../components/StockAdjustmentPanel";
import { StockChangeLogs } from "../components/StockChangeLogs";
import { useGuardrailsFeatureFlag } from "../hooks/useGuardrailsFeatureFlag";
import { useStockCorrectionActions } from "../stores/useStockCorrectionActions";
import { useStockCorrectionStore } from "../stores/useStockCorrectionStore";

export function DeclareStockCorrectionPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { state: locationState } = useLocation() as {
    state: { sku: string; origin: PageName; proposalId: string } | null;
  };
  const { showToastUI } = useCustomToast();

  const phase = useStockCorrectionStore((s) => s.phase);
  const inventoryProductsList = useStockCorrectionStore((s) => s.inventoryProductsList);
  const selectedInventoryProductSku = useStockCorrectionStore((s) => s.selectedInventoryProductSku);
  const stockCorrectionId = useStockCorrectionStore((s) => s.stockCorrectionId);

  const inventoryUnit = inventoryProductsList.find(
    (p) => p.productSku === selectedInventoryProductSku,
  );

  const { sendSegmentTrackEvent } = useAnalytics();
  const { isNavigatingToStockCorrection, productSku } = useRestockingServiceStore();
  const { submitStockUpdate, scanProductFromText, goBackToIdle } = useStockCorrectionActions();

  const {
    isOpen: isProductNotAssignedForTheHubErrorModalOpen,
    onOpen: openProductNotAssignedForTheHubErrorModal,
    onClose: closeProductNotAssignedForTheHubErrorModal,
  } = useDisclosure();

  const {
    isOpen: isStockChangeConfirmationModalOpen,
    onOpen: openStockChangeConfirmationModal,
    onClose: closeStockChangeConfirmationModal,
  } = useDisclosure();

  const { data, loading: isGetProductStockLoading } = useGetProductStockQuery({
    fetchPolicy: "network-only",
    variables: { sku: inventoryUnit?.productSku!, withPrice: true, isAlwaysInbound: true },
    skip: !inventoryUnit?.productSku,
    onCompleted: (result) => {
      if (!result.getProduct.inventoryEntry.stock) {
        openProductNotAssignedForTheHubErrorModal();
      }
    },
    onError: (error) => {
      if (error?.graphQLErrors?.[0]?.extensions?.code === "PRODUCT_NOT_FOUND") {
        openProductNotAssignedForTheHubErrorModal();
      }
    },
  });
  const product = data?.getProduct;
  const hasLoadedStock = product?.inventoryEntry?.stock?.shelf !== undefined;
  const initialStock = hasLoadedStock ? product!.inventoryEntry.stock!.shelf : 0;
  const productPrice = product?.price ?? 0;
  const countryOfOriginName = useCountryNames(inventoryUnit?.countryOfOriginCode ?? null);

  const [selectedReason, setSelectedReason] = useState<InventoryChangeReason | null>(null);
  const [counter, setCounter] = useState(0);

  const isDeclareStockCorrectionsLoading = phase === "declareStockCorrectionsLoading";
  const isSearchLoading = phase === "searchLoading";

  const { isFeatureEnabled: blockCorrectionsInEOY } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.EOY_HIDE_CORRECTIONS,
  );
  const guardrails = useGuardrailsFeatureFlag();
  const quantityThreshold = guardrails?.quantityThreshold;
  const valueThreshold = guardrails?.valueThresholds;
  const quantityChangeLimit = guardrails?.quantityChangeLimit;

  useEffect(() => {
    if (locationState?.origin) {
      useStockCorrectionStore.getState().setOriginInfo({
        originPage: locationState?.origin,
        proposalId: locationState?.proposalId,
      });
    }

    if (locationState?.origin === PageName.INVENTORY_SHELF_CHANGES_PAGE && locationState?.sku) {
      scanProductFromText(locationState.sku);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNavigatingToStockCorrection && productSku) {
      scanProductFromText(productSku);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finalStockCount = useMemo(() => {
    if (selectedReason === InventoryChangeReason.correction) {
      return initialStock + counter;
    }
    return initialStock - counter;
  }, [selectedReason, counter, initialStock]);

  const isStockUpdated = useMemo(
    () => initialStock !== finalStockCount,
    [initialStock, finalStockCount],
  );
  const isIncreaseBtnDisabled = useMemo(() => finalStockCount === 0, [finalStockCount]);

  const isCorrection = useMemo(() => {
    return selectedReason === InventoryChangeReason.correction;
  }, [selectedReason]);

  const quantityExceeded = useMemo(() => {
    return isCorrection && quantityThreshold && Math.abs(counter) >= quantityThreshold;
  }, [isCorrection, quantityThreshold, counter]);

  const valueExceeded = useMemo(() => {
    return isCorrection && valueThreshold && Math.abs(counter * productPrice) >= valueThreshold;
  }, [isCorrection, valueThreshold, counter, productPrice]);

  const componentName: string | undefined = useMemo(() => {
    if (quantityExceeded) {
      return GuardrailMap.QuantityExceeded;
    }
    if (valueExceeded) {
      return GuardrailMap.ValueExceeded;
    }
    return undefined;
  }, [quantityExceeded, valueExceeded]);

  const onSubmit = useCallback(() => {
    submitStockUpdate({
      initial: initialStock,
      reason: selectedReason,
      counter,
      newTotalStock: finalStockCount,
    });
  }, [counter, initialStock, finalStockCount, selectedReason, submitStockUpdate]);

  const handleStockChange = () => {
    closeStockChangeConfirmationModal();
    onSubmit();
    sendSegmentTrackEvent("guardrail_progressed", {
      action: "confirm",
      process_id: stockCorrectionId ?? undefined,
      product_sku: inventoryUnit?.productSku,
      attempted_quantity: counter,
      screen_name: PageName.INVENTORY_PAGE,
      guardrail_type: componentName,
    });
  };

  const handleCancelStockChange = () => {
    closeStockChangeConfirmationModal();
    sendSegmentTrackEvent("guardrail_progressed", {
      action: "cancel",
      process_id: stockCorrectionId ?? undefined,
      product_sku: inventoryUnit?.productSku,
      attempted_quantity: counter,
      screen_name: PageName.INVENTORY_PAGE,
      guardrail_type: componentName,
    });
  };

  const isSubmitDisabled = useMemo(() => {
    if (!inventoryUnit) return true;
    if (!hasLoadedStock) return true;
    if (!selectedReason) return true;
    return false;
  }, [inventoryUnit, hasLoadedStock, selectedReason]);

  const onHandleSubmit = () => {
    if (isSubmitDisabled) return;
    if (quantityChangeLimit !== undefined) {
      const isOverLimit = Math.abs(counter) > quantityChangeLimit;
      if (isOverLimit) {
        setCounter(0);
        showToastUI?.({
          id: "quantity-Limit-eceeded",
          status: "info",
          title: "quantity-Limit-eceeded-title",
          description: intl.formatMessage(
            { id: "quantity-Limit-eceeded-description" },
            { quantityChangeLimit },
          ),
        });
        return;
      }
    }
    if (quantityExceeded || valueExceeded) {
      openStockChangeConfirmationModal();

      sendSegmentTrackEvent("click", {
        screen_name: PageName.INVENTORY_PAGE,
        component_name: componentName,
        component_value: inventoryUnit?.productSku,
        component_content: stockCorrectionId ?? undefined,
      });

      return;
    }

    onSubmit();
  };

  useEffect(() => {
    if (!hasLoadedStock) return;
    if (blockCorrectionsInEOY) return;
    if (selectedReason !== null) return;

    if (initialStock === 0) {
      setSelectedReason(InventoryChangeReason.correction);
    }
  }, [hasLoadedStock, blockCorrectionsInEOY, initialStock, selectedReason]);
  return (
    <Page isFull isLoading={isGetProductStockLoading || isSearchLoading} isBgGrey h="100%">
      <NavigationHeaderShelfNumber
        title={intl.formatMessage({
          id: "components.inventory.declare-stock-corrections.header-title",
        })}
        shelfLetter={inventoryUnit?.shelfLetter ?? null}
        shelfNumber={inventoryUnit?.shelfNumber ?? null}
        onClickGoBack={goBackToIdle}
      />
      {isNotNullNorUndefined(inventoryUnit) && isNotNullNorUndefined(inventoryUnit.productName) && (
        <Box p="s200" bg="white" w="100%">
          <ProductInformation
            p={0}
            variantSize="s"
            imageUrl={inventoryUnit.productImageUrl}
            name={inventoryUnit.productName}
            countryOfOrigin={countryOfOriginName}
            stockStatus={isStockUpdated ? "updated" : "unchanged"}
            sku={inventoryUnit.productSku}
          />
          <Divider my="s100" />
          <ProductFacing
            isShelvedInHandlingUnits={!!product?.isShelvedInHandlingUnits}
            numberOfFacings={product?.numberOfShelfFacings ?? 0}
            numberOfStacking={product?.numberOfShelfStackings ?? null}
          />
        </Box>
      )}
      <Box flex={1} flexDirection="column" w="100%" overflowY="scroll">
        <ProductNotAssignedForTheHubErrorModal
          onClickYes={() => {
            closeProductNotAssignedForTheHubErrorModal();
            navigate(routes.inbound.root);
          }}
          onClickNo={() => {
            closeProductNotAssignedForTheHubErrorModal();
            goBackToIdle();
          }}
          isOpen={isProductNotAssignedForTheHubErrorModalOpen}
        />
        <StockChangeConfirmationModal
          isOpen={isStockChangeConfirmationModalOpen}
          currentValue={counter}
          onClickYes={handleStockChange}
          onClickNo={handleCancelStockChange}
        />
        <Flex direction="column" w="100%" flex={1} justify="flex-start" p="s200" gap="s200">
          <StockAdjustmentPanel
            initialStock={initialStock}
            newStock={finalStockCount}
            isIncreaseBtnDisabled={isIncreaseBtnDisabled}
            selectedReason={selectedReason}
            setSelectedReason={setSelectedReason}
            counter={counter}
            setCounter={setCounter}
          />
          {inventoryUnit?.productSku && <StockChangeLogs sku={inventoryUnit?.productSku} />}
        </Flex>
      </Box>
      <Box bg="white" p="s200" w="100%" borderTopWidth="1px" borderColor="grey.200">
        <Button
          onClick={onHandleSubmit}
          disabled={isSubmitDisabled}
          isLoading={isDeclareStockCorrectionsLoading}
          data-testid="stock-correction-submit-btn"
          size="lg"
          isFullWidth
        >
          {intl.formatMessage({
            id: "components.inventory.declare-stock-corrections.confirm-stock",
          })}
        </Button>
      </Box>
    </Page>
  );
}
